import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel"; // 조회조건 Panel
import { fnGetDataSet, fnSave, fnGetTableById } from "@shared/api/restApi"; // BackEnd API
import { fnCheckDuplicateSn } from "@shared/utils/InvUtils";
import { getInitFormData, getAutoSlip, CalAm } from "@shared/utils/CommonUtils"; // 공통 유틸 메뉴 초기화(조회조건, 그리드, 프리폼) 설정 load 관련 내용
import { fnGetIoFgTrns } from "@shared/utils/DrilldownUtils";
import SnTab from "@shared/components/SnTab";
import SnTabHeader from "@shared/components/SnTabHeader";
import SnFileUploadBox from "@shared/components/SnFileUploadBox";
import { showDialog } from "@widgets/CommonDialog"; // Message Box
import { global, ExecuteRunner } from "@shared/libs/Global"; // Login User관련 전역 변수( 로그인 정보)
import SnGrid from "@shared/components/GridV2/SnGrid"; // 공통 그리드
import { menuInfo } from "@shared/type/CommonType"; // 공통 Interface
import SnResizeBar from "@shared/components/SnResizeBar";
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";
import { dateToString } from "@shared/utils/DateUtils";
import { RowNode } from "ag-grid-community";
import SnBarcodeBox from "@shared/components/SnBarcodeBox";
import SnCodeBox from "@shared/components/SnCodeBox";
import SnLabel from "@shared/components/SnLabel";
import SnDateBox from "@shared/components/SnDateBox";
import SnButton from "@shared/components/SnButton";

interface Props {
  Menuprops: menuInfo;
}

interface CodeValue {
  code: string;
  name: string;
}

interface TabState {
  page: string;
  searchParams: any | null;
}

//#region  거래구분별 컬럼 전환 (수입 = BL/통관 기준)
/**
 * 거래구분 4·5 는 통관이 완료된 구매입고의뢰를 기준으로 조회한다(쿼리에서 처리).
 * 화면은 기준이 달라진 만큼 제목과 컬럼을 바꾼다. 거래구분은 조회 필수값이라
 * 한 그리드에 국내·수입이 섞이지 않는다.
 */
const IMPORT_TRNS = ["4", "5"];
/** 수입일 때 바뀌는 제목. 같은 컬럼이 다른 의미로 쓰인다. */
const IMPORT_TITLE: { [key: string]: string } = {
  no_req: "BL번호",
  dt_req: "선적일",
};
/** 수입일 때만 보이는 컬럼. 평소엔 메타의 fg_visible='N' 이라 숨어 있다. */
const IMPORT_COLUMNS: { [key: string]: string[] } = {
  grid1: ["no_ip", "dt_custom"],
  grid2: ["no_ip", "no_line_ip"],
};
/** 위 컬럼을 끼울 자리 — 이 컬럼 바로 뒤. */
const IMPORT_ANCHOR: { [key: string]: string } = {
  grid1: "dt_req",
  grid2: "no_line_lc",
};
//#endregion

const SN_MTL_RCV_REG_004 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region  variable
  const gridRefs = useRef<{ [key: string]: React.RefObject<SnGridRef> }>({
    grid1: React.createRef<SnGridRef>(),
    grid2: React.createRef<SnGridRef>(),
    grid3: React.createRef<SnGridRef>(),
    grid4: React.createRef<SnGridRef>(),
    grid5: React.createRef<SnGridRef>(),
    grid6: React.createRef<SnGridRef>(),
  });

  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };

  const searchRef = useRef<any>(null);

  const txtBarcodeRef = useRef<any>(null);
  let isScan = useRef<Boolean>(false); // 바코드 스캔한 건인지 여부

  const isFirstRender = useRef(true); // 화면 오픈시 조회 방지

  // 처리 후 '처리' 탭으로 이동할 때 조회조건의 입고일을 오늘로 덮어쓰므로,
  // 덮어쓰기 직전의 '미처리' 조회조건을 보관했다가 미처리 탭으로 돌아올 때 되돌린다.
  const prevSearchParams = useRef<any>(null);

  const { userInfo, config } = global(); // Login user 정보
  //#endregion

  // 처리 컴포넌트
  const [dateBox, setDateBoxValue] = useState<string>(dateToString(new Date()));
  const [codeWh, setWhCode] = useState<CodeValue>({ code: "", name: "" });
  const [codeEmp, setEmpCode] = useState<CodeValue>({
    code: userInfo.cd_emp || "",
    name: userInfo.nm_emp || "",
  });

  const [tabPageState, setTabState] = useState<TabState>({
    page: "1",
    searchParams: null,
  });

  const dateValueChanged = async (value: string) => {
    setDateBoxValue(value);
  };

  const whValueChanged = async (code: string, name: string, rowData?: any) => {
    setWhCode({ code: code, name: name });
  };

  const empValueChanged = async (code: string, name: string, rowData?: any) => {
    setEmpCode({ code: code, name: name });
  };

  //#region  Components useState
  const [formData, setFormData] = useState<any>(); // 조회조건 변수
  const [formControlData, setFormControlData] = useState<any[]>([]); // 조회조건 설정된 항목 binding
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
    grid4: [],
    grid5: [],
    grid6: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
    grid4: [],
    grid5: [],
    grid6: [],
  });
  const [selectedDocu, setSelectedDocu] = useState<string>("");
  /** 메타로 받은 원본 컬럼(미처리 탭). 거래구분에 따라 여기서 다시 만든다. */
  const baseColsRef = useRef<{ [key: string]: any[] }>({});
  //#endregion

  /**
   * 거래구분에 맞춰 컬럼을 만든다.
   * 국내(1~3)면 메타 그대로 — 신규 컬럼은 fg_visible='N' 이라 안 보인다.
   * 수입(4·5)이면 제목을 바꾸고 신고번호·신고일(항번)을 앵커 컬럼 뒤에 끼운다.
   * no_seq 사이에 빈 자리가 없어 메타로는 위치를 못 잡아 여기서 순서를 다시 매긴다.
   */
  const buildTrnsColumns = (gridKey: string, isImport: boolean) => {
    const base = baseColsRef.current[gridKey] ?? [];
    if (!isImport || base.length === 0) return base;

    const extraKeys = IMPORT_COLUMNS[gridKey] ?? [];
    const sorted = [...base].sort(
      (a: any, b: any) => Number(a.no_seq ?? 0) - Number(b.no_seq ?? 0)
    );

    const extras = extraKeys
      .map((key) => sorted.find((c: any) => c.cd_column === key))
      .filter(Boolean)
      .map((c: any) => ({ ...c, fg_visible: "Y" }));

    const rest = sorted
      .filter((c: any) => !extraKeys.includes(c.cd_column))
      .map((c: any) =>
        IMPORT_TITLE[c.cd_column]
          ? { ...c, nm_column: IMPORT_TITLE[c.cd_column] }
          : c
      );

    const anchor = rest.findIndex(
      (c: any) => c.cd_column === IMPORT_ANCHOR[gridKey]
    );
    const cut = anchor < 0 ? rest.length : anchor + 1;

    return [...rest.slice(0, cut), ...extras, ...rest.slice(cut)].map(
      (c: any, i: number) => ({ ...c, no_seq: i + 1 })
    );
  };

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        // 초기값에 공유로 받은 값이 있으면 덮어쓰지 않고 유지
        setFormData((prev: any) => ({
          ...(rtn.formData || {}),
          ...(prev || {}),
        }));
        // 조회할 때마다 거래구분에 맞춰 다시 만들어야 하므로 메타 원본을 따로 들고 있는다.
        baseColsRef.current = {
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
        };
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
          grid3: rtn.grid.grid3,
          grid4: rtn.grid.grid4,
          grid5: rtn.grid.grid5,
          grid6: rtn.grid.grid6,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  const drilldownHandledRef = useRef<any>(null);
  useEffect(() => {
    if (
      Menuprops.figure &&
      columns.grid1.length > 0 &&
      columns.grid2.length > 0
    ) {
      // 조회·팝업 등으로 컬럼이 다시 만들어질 때마다 드릴다운이 재실행되지 않게(처리탭으로 되돌아가는 문제) 드릴다운 1건당 1번만.
      if (drilldownHandledRef.current !== Menuprops) {
        drilldownHandledRef.current = Menuprops;
        onActivate(Menuprops.nodeData);
      }
    }
  }, [columns, Menuprops]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (tabPageState.page) {
      if (!tabPageState.searchParams) {
        onSearch(null);
      } else {
        onSearch(tabPageState.searchParams);
      }
    }
  }, [tabPageState.page]);

  //#region  Button Event
  useImperativeHandle(ref, () => ({
    onSearch,
    onExecute,
  }));

  // 미처리 조회 시 검사관련 표시/수량 세팅
  //  - 검사대상 아님(yn_qc!=='Y')     : 검사상태/합격여부 공란 (수량은 쿼리값=전량 양품)
  //  - 검사완료(yn_qc='Y'&&st_qc='C') : 불량=qt_qc_bad, 양품=qt-불량, 입고=qt
  //    (SN품목은 스캔으로 수량관리하므로 제외)
  //  - 검사 미완료(yn_qc='Y'&&st_qc<>'C') : 수량 0 유지 (처리 시 중단)
  const prepQcRows = (rows: any[]) =>
    (rows || []).map((r: any) => {
      if (r?.yn_qc !== "Y") {
        return { ...r, st_qc: "", yn_confirm: "" };
      }
      if (r.st_qc === "C" && r.fg_unit !== "SN") {
        const bad = Number(r.qt_qc_bad) || 0;
        const qt = Number(r.qt) || 0;
        return { ...r, qt_bad: bad, qt_good: qt - bad, qt_rcv: qt };
      }
      return r;
    });

  //#region onSearch
  const onSearch = async (searchData: any) => {
    try {
      if (tabPageState.page === "1") {
        const searchDataToUse = searchData || searchRef.current.getFormdata();
        const params = {
          id: "sn_mtl_rcv_reg_004_01",
          params: searchDataToUse,
        };

        resetCondition();

        getGridCurrent("grid1").resetUpdate();
        getGridCurrent("grid2").resetUpdate();
        getGridCurrent("grid3").resetUpdate();
        const data: any = await fnGetDataSet(params);

        // 조회조건의 거래구분으로 컬럼을 맞춘다(거래구분은 필수값이라 항상 값이 있다).
        const isImport = IMPORT_TRNS.includes(
          String(searchDataToUse.fg_trns ?? "")
        );
        setColums((prev: any) => ({
          ...prev,
          grid1: buildTrnsColumns("grid1", isImport),
          grid2: buildTrnsColumns("grid2", isImport),
        }));

        setDataSources({
          grid1: data[0],
          grid2: prepQcRows(data[1] || []),
          grid3: data[2] || [],
          grid4: [],
          grid5: [],
          grid6: [],
        });
      } else {
        const searchDataToUse = searchData || searchRef.current.getFormdata();
        const params = {
          id: "sn_mtl_rcv_reg_004_02",
          params: searchDataToUse,
        };

        getGridCurrent("grid5").resetUpdate();
        getGridCurrent("grid6").resetUpdate();
        const data: any = await fnGetDataSet(params);

        setDataSources({
          grid1: [],
          grid2: [],
          grid3: [],
          grid4: data[0],
          grid5: data[1],
          grid6: data[2] || [],
        });
      }
    } catch (error: any) {}
  };
  //#endregion

  //#region onExecute
  const onExecute = async (id: string) => {
    let hasInvalid = false;

    try {
      switch (id) {
        case "Save": // 처리
          {
            const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
            if (!focusedRowNodeH) {
              return;
            }

            if (dateBox === "") {
              await showDialog("alter", "입고일을 입력해 주세요.");
              return false;
            }

            if (codeEmp.code === "") {
              await showDialog("alter", "담당자를 입력해 주세요.");
              return false;
            }

            let hasInvalid = false;

            // 수입검사 진행상태 체크 (SN품목 포함)
            //  - yn_qc='Y' && st_qc!='C'                  : 검사 미완료 → 중단
            //  - yn_qc='Y' && st_qc='C' && yn_confirm='N' : 불합격 → 확인 후 해당 라인(및 SN) 제외
            //  (입고: yn_qc='N' 또는 yn_qc='Y'&&st_qc='C'&&yn_confirm IN('Y'합격,'R'특채) / 제외: yn_confirm='N'만)
            const isQcIncomplete = (d: any) =>
              d.yn_qc === "Y" && d.st_qc !== "C";
            const isQcReject = (d: any) =>
              d.yn_qc === "Y" && d.st_qc === "C" && d.yn_confirm === "N";

            let hasQcIncomplete = false;
            let hasQcReject = false;
            const excludedLineKeys = new Set<any>(); // 제외할 라인의 no_line (SN 제외에도 사용)
            getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
              if (isQcIncomplete(node.data)) hasQcIncomplete = true;
              if (isQcReject(node.data)) {
                hasQcReject = true;
                excludedLineKeys.add(node.data.no_line);
              }
            });

            if (hasQcIncomplete) {
              await showDialog(
                "alter",
                "수입검사가 완료되지 않은 건이 존재합니다. 검사상태를 확인해주시기 바랍니다."
              );
              return false;
            }

            if (hasQcReject) {
              const proceed = await showDialog(
                "confirm",
                "수입검사 시 불합격한 품목이 존재합니다.\n불합격한 내역은 제외한 후 입고처리 됩니다.\n진행하시겠습니까?"
              );
              if (!proceed) return false;
            }

            const MTL0013 = config?.MTL0013?.dc_ctrl ?? "";

            // 자동채번 아니면 LOT입력여부 체크
            if (MTL0013 === "N") {
              getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
                if (
                  !isQcReject(node.data) &&
                  node.data.fg_unit === "LOT" &&
                  (node.data.no_lot === null ||
                    node.data.no_lot === undefined ||
                    node.data.no_lot === "")
                ) {
                  hasInvalid = true;
                  return false;
                }
              });

              if (hasInvalid) {
                await showDialog(
                  "alter",
                  "LOT관리 품목의 LOT가 입력되지 않았습니다."
                );
                return false;
              }
            }

            const localDuplicates: string[] = []; // 내부 중복된 시리얼 번호를 담을 배열
            const seen = new Set<string>(); // 비교를 위한 임시 셋
            const snCheckList: any[] = [];

            getGridCurrent("grid3").forEachNode((node: RowNode) => {
              if (excludedLineKeys.has(node.data.no_line)) return; // 불합격 라인 SN 제외
              snCheckList.push({
                cd_item: node.data.cd_item,
                no_sn: node.data.no_sn,
                cd_biz: node.data.cd_biz,
              });

              const identifier = `${node.data.cd_item}_${node.data.no_sn.toLowerCase()}`; // 품목과 시리얼의 조합 키 생성

              if (node.data.no_sn === "") {
                hasInvalid = true;
              }
              if (seen.has(identifier)) {
                // 이미 셋에 존재한다면 화면에 입력된 데이터들끼리 중복된 것임
                if (!localDuplicates.includes(node.data.no_sn.toLowerCase())) {
                  localDuplicates.push(node.data.no_sn.toLowerCase());
                }
              } else {
                seen.add(identifier);
              }

              return {
                cd_item: node.data.cd_item,
                no_sn: node.data.no_sn,
                cd_biz: node.data.cd_biz,
              };
            });

            if (hasInvalid) {
              await showDialog(
                "alter",
                "시리얼 번호가 입력되지 않은 건이 있습니다."
              );
              return false;
            }

            if (localDuplicates.length > 0) {
              await showDialog(
                "alter",
                `입력된 목록 중 중복된 시리얼 번호가 있습니다.\n${localDuplicates.join(", ")}`
              );
              return false;
            }

            if (snCheckList.length > 0) {
              const duplicateSns = await fnCheckDuplicateSn(snCheckList);

              if (duplicateSns && duplicateSns.length > 0) {
                await showDialog(
                  "alter",
                  `이미 등록된 시리얼 번호가 포함되어 있습니다.\n${duplicateSns.join(", ")}`
                );
                return false;
              }
            }

            let hasInvalidcdItem = "";

            getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
              if (isQcReject(node.data)) return; // 불합격 제외
              if (node.data.qt !== node.data.qt_rcv) {
                if (!hasInvalid) {
                  hasInvalid = true;
                  hasInvalidcdItem = node.data.cd_item;
                }
              }
            });

            if (hasInvalid) {
              await showDialog(
                "alter",
                "품목코드 [" +
                  hasInvalidcdItem +
                  "]의 의뢰수량과 입고수량이 같지 않습니다."
              );
              return false;
            }

            const cd_module: any = Menuprops.cd_module;
            const no_slip = await getAutoSlip(
              cd_module,
              9,
              dateBox,
              Menuprops.cd_menu
            );

            const qtioH: any[] = [];
            const qtio: any[] = [];
            const qtioSn: any[] = [];

            const insertDataH = {
              cd_firm: userInfo.cd_firm,
              no_io: "",
              cd_biz: focusedRowNodeH.data.cd_biz,
              cd_wh: codeWh.code ? codeWh.code : "",
              dt_io: dateBox,
              cd_cust: focusedRowNodeH.data.cd_cust,
              cd_exch: focusedRowNodeH.data.cd_exch,
              rt_exch: focusedRowNodeH.data.rt_exch,
              cd_io: focusedRowNodeH.data.cd_io,
              fg_slip: "1",
              tp_slip: "1",
              fg_io: focusedRowNodeH.data.fg_io,
              fg_money: focusedRowNodeH.data.fg_money,
              fg_trns: focusedRowNodeH.data.fg_trns,
              // cd_dept: focusedRowNodeH.data.cd_dept,
              cd_emp: codeEmp.code,
              // cd_gp: focusedRowNodeH.data.cd_gp,
              tp_trans: focusedRowNodeH.data.tp_trans,
              cd_pjt: focusedRowNodeH.data.cd_pjt,
              cd_mgmtdtl: focusedRowNodeH.data.cd_mgmtdtl,
              dc_rmk: focusedRowNodeH.data.dc_rmk,
              am: focusedRowNodeH.data.am,
              am_k: focusedRowNodeH.data.am_k,
              am_vat: focusedRowNodeH.data.am_vat,
              am_supply: focusedRowNodeH.data.am_supply,
            };

            qtioH.push(insertDataH);

            getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
              if (isQcReject(node.data)) return; // 불합격 라인 입고 제외
              const insertDataL = {
                cd_firm: userInfo.cd_firm,
                no_io: "",
                no_line: node.data.no_line,
                cd_biz: focusedRowNodeH.data.cd_biz,
                cd_wh: codeWh.code ? codeWh.code : node.data.cd_wh,
                cd_io: focusedRowNodeH.data.cd_io,
                fg_slip: "1",
                tp_slip: "1",
                fg_io: focusedRowNodeH.data.fg_io,
                fg_money: focusedRowNodeH.data.fg_money,
                cd_item: node.data.cd_item,
                yn_lot: node.data.yn_lot,
                qt_good: node.data.qt_good,
                qt_bad: node.data.qt_bad,
                qt: node.data.qt,
                um: node.data.um,
                am: node.data.am,
                rt_exch: node.data.rt_exch,
                um_k: node.data.um_k,
                am_k: node.data.am_k,
                am_vat: node.data.am_vat,
                fg_vat: node.data.fg_vat,
                um_supply: node.data.um_supply,
                am_supply: node.data.am_supply,
                dt_rtn_plan: node.data.dt_rtn_plan,
                no_order: node.data.no_po,
                no_line_order: node.data.no_line_po,
                no_req: node.data.no_req,
                no_line_req: node.data.no_line,
                no_lc: node.data.no_lc,
                no_line_lc: node.data.no_line_lc,
                // 수입통관 신고번호·신고항번. 국내(1~3)는 tra_ipl 조인이 없어 null 로 온다.
                // mtl_qtio.no_line_ip 는 NOT NULL 이라 null 을 그대로 보내면 엔티티 기본값(0)을
                // 덮어써서 저장이 실패한다.
                no_ip: node.data.no_ip,
                no_line_ip: node.data.no_line_ip ?? 0,
                dc_rmk: node.data.dc_rmk,
                dt_io: dateBox,
                no_lot: node.data.no_lot,
                no_lot_cust: node.data.no_lot_cust,
                cd_pjt: node.data.cd_pjt,
                no_wbs: node.data.no_wbs,
                no_cbs: node.data.no_cbs,
                qt_lotsize: node.data.qt_lotsize,
                cd_item_cust: node.data.cd_item_cust,
              };
              qtio.push(insertDataL);
            });

            getGridCurrent("grid3").forEachNode((node: RowNode) => {
              if (excludedLineKeys.has(node.data.no_line)) return; // 불합격 라인 SN 제외
              if (node.data.no_req == focusedRowNodeH.data.no_req) {
                const qtioD = {
                  no_io: "",
                  no_line: node.data.no_line,
                  no_sn: node.data.no_sn,
                };
                qtioSn.push(qtioD);
              }
            });

            if (qtio.length === 0) {
              await showDialog("alter", "입고 처리할 내역이 없습니다.");
              return false;
            }

            // 불합격 라인 제외 시 헤더 금액을 남은 라인 합계로 재계산
            if (hasQcReject) {
              const sumBy = (k: string) =>
                qtio.reduce((a, r) => a + (Number(r[k]) || 0), 0);
              qtioH[0].am = sumBy("am");
              qtioH[0].am_k = sumBy("am_k");
              qtioH[0].am_vat = sumBy("am_vat");
              qtioH[0].am_supply = sumBy("am_supply");
            }

            qtioH[0].no_io = no_slip;

            for (let row of qtio) {
              row.no_io = no_slip;
            }

            for (let row of qtioSn) {
              row.no_io = no_slip;
            }

            // 요청 데이터 구성
            const SaveData = {
              qtioh: { insert: qtioH },
              qtio: { insert: qtio },
              qtio_sn: { insert: qtioSn },
            };

            // 저장 API 호출
            const ok = await fnSave("SN_MTL_RCV_REG_004_SAVE_01", SaveData);
            if (!ok) return false;

            // 성공적으로 저장된 후 초기화
            getGridCurrent("grid1").resetUpdate();
            getGridCurrent("grid2").resetUpdate();
            getGridCurrent("grid3").resetUpdate();
            resetCondition();

            const MTL0010 = config?.MTL0010?.dc_ctrl ?? "";

            switch (MTL0010) {
              case "Y": {
                // 처리로 이동
                const currentSearch =
                  searchRef.current?.getFormdata() ?? formData;
                // 입고일로 덮어쓰기 전 조회조건 보관 (미처리 탭 복귀시 복원)
                prevSearchParams.current = { ...currentSearch };

                const rcvDate = {
                  ...currentSearch,
                  dt_from: dateBox,
                  dt_to: dateBox,
                };
                setFormData(rcvDate);

                setTabState({ page: "2", searchParams: rcvDate });
                break;
              }
              default: // 재조회
                onSearch(null);
                break;
            }
          }
          break;
        case "Delete": // 취소
          {
            const focusedRowNodeH = getGridCurrent("grid4").focusedRowNode();
            if (!focusedRowNodeH) {
              return;
            }

            getGridCurrent("grid5").forEachFilteredNode((node: RowNode) => {
              if (node.data.cls > 0) {
                if (!hasInvalid) {
                  hasInvalid = true;
                }
              }
            });

            if (hasInvalid) {
              await showDialog(
                "alter",
                "마감 처리된 건이 존재하여 취소 할 수 없습니다"
              );
              return false;
            }

            const confirm = await showDialog("confirm", "취소하시겠습니까?");

            if (!confirm) {
              return; // 사용자가 아니오를 선택하면 함수 종료
            }

            getGridCurrent("grid6").applyFilters({
              no_io: focusedRowNodeH.data.no_io,
            });

            getGridCurrent("grid6").deleteAllRows();
            getGridCurrent("grid5").deleteAllRows();
            getGridCurrent("grid4").deleteSelectedRows();

            const qtiohChanges = getGridCurrent("grid4").getChange();
            const qtioChanges = getGridCurrent("grid5").getChange();
            const qtioSnChanges = getGridCurrent("grid6").getChange();

            // 요청 데이터 구성
            const SaveData = {
              qtioh: qtiohChanges,
              qtio: qtioChanges,
              qtio_sn: qtioSnChanges,
            };

            // 저장 API 호출
            const ok = await fnSave("SN_MTL_RCV_REG_004_SAVE_01", SaveData);
            if (!ok) return false;

            // 성공적으로 저장된 후 초기화
            getGridCurrent("grid4").resetUpdate();
            getGridCurrent("grid5").resetUpdate();
            getGridCurrent("grid6").resetUpdate();

            setTabState({
              page: "1",
              searchParams: restorePrevSearchParams(),
            });
          }
          break;
      }
    } catch (error: any) {
      getGridCurrent("grid1").resetUpdate(true);
      getGridCurrent("grid2").resetUpdate(true);
      getGridCurrent("grid4").resetUpdate(true);
      getGridCurrent("grid5").resetUpdate(true);
      getGridCurrent("grid6").resetUpdate(true);
    }
  };
  //#endregion

  //#region onActivate
  const onActivate = async (nodeData: any) => {
    if (!nodeData) return;
    if ((Menuprops.figure as string) === "fg_drilldown.Search") {
      // 거래구분이 조회 필수라 수불번호의 거래구분을 같이 넣는다(없으면 기본값 국내로 조회돼 0건).
      const fg_trns = await fnGetIoFgTrns(nodeData.no_io);
      const selectedData = {
        ...(fg_trns ? { fg_trns } : {}),
        cd_firm: nodeData.cd_firm,
        cd_biz: nodeData.cd_biz,
        no_io: nodeData.no_io,
        dt_from: nodeData.dt_io,
        dt_to: nodeData.dt_io,
      };
      const searchData = {
        ...formData,
        ...selectedData,
      };
      setFormData(searchData);
      // 이미 "처리" 탭이면 page 가 그대로라 [tabPageState.page] effect 가 안 돈다 → 여기서 바로 재조회
      if (tabPageState.page === "2") onSearch(searchData);
      setTabState({ page: "2", searchParams: searchData });
    }
  };
  //#endregion

  //#region GridL Events
  const onCellValueChangedL = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    switch (colId) {
      case "qt_good":
        rowNode.setDataValue("qt_bad", rowData.qt - rowData.qt_good, "noEvent");
        rowNode.setDataValue(
          "qt_rcv",
          rowData.qt_good + rowData.qt_bad,
          "noEvent"
        );
        CalAm(gridRefs.current["grid1"], gridRefs.current["grid2"], "rt_exch");
        break;
      case "qt_bad":
        rowNode.setDataValue("qt_good", rowData.qt - rowData.qt_bad, "noEvent");
        rowNode.setDataValue(
          "qt_rcv",
          rowData.qt_good + rowData.qt_bad,
          "noEvent"
        );
        CalAm(gridRefs.current["grid1"], gridRefs.current["grid2"], "rt_exch");
        break;
    }
  };
  //#endregion

  //#region GridD Events
  const onBeforeAddRowD = (): any => {
    try {
      const focusedRowNode = getGridCurrent("grid2").focusedRowNode();

      const updatedRow = {
        cd_firm: focusedRowNode.data?.cd_firm,
        cd_biz: focusedRowNode.data?.cd_biz,
        no_req: focusedRowNode.data?.no_req,
        no_line: focusedRowNode.data?.no_line,
        cd_item: focusedRowNode.data?.cd_item,
        no_sn: isScan.current == true ? txtBarcodeRef.current.getValue() : "",
      };

      focusedRowNode.setDataValue(
        "qt_good",
        Number(focusedRowNode.data?.qt_good) + 1,
        "noEvent"
      );
      focusedRowNode.setDataValue(
        "qt_rcv",
        Number(focusedRowNode.data?.qt_rcv) + 1,
        "noEvent"
      );

      isScan.current = false;

      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };

  const onRowDataUpdatedSn = () => {
    const focusedRowNodeD = getGridCurrent("grid3").focusedRowNode();

    if (focusedRowNodeD) {
      if (
        focusedRowNodeD.data.no_sn === "" ||
        focusedRowNodeD.data.no_sn === null ||
        focusedRowNodeD.data.no_sn === undefined
      ) {
        return;
      }
    }
    txtBarcodeRef.current.focus();
  };

  const onGridDBtnsClick = async (id: string) => {
    const focusedRowNodeL = getGridCurrent("grid2").focusedRowNode();

    if (focusedRowNodeL.data.fg_unit !== "SN") return false;

    switch (id) {
      case "addRow":
        let hasInvalid = false;

        getGridCurrent("grid3").forEachFilteredNode((node: RowNode) => {
          if (node.data.no_sn === "") {
            hasInvalid = true;
          }
        });
        if (hasInvalid) {
          return;
        } else {
          return getGridCurrent("grid3").addRow();
        }
      case "deleteRow":
        if (getGridCurrent("grid3").getDisplayedRowCount() < 1) {
          return;
        }
        getGridCurrent("grid3").deleteSelectedRows();

        focusedRowNodeL.setDataValue(
          "qt_good",
          Number(focusedRowNodeL.data?.qt_good) - 1,
          "noEvent"
        );
        focusedRowNodeL.setDataValue(
          "qt_rcv",
          Number(focusedRowNodeL.data?.qt_rcv) - 1,
          "noEvent"
        );
        break;
    }
  };
  //#endregion

  //#region onBarcodeEnter
  const onBarcodeEnter = async (e: any) => {
    if (getGridCurrent("grid2").getDisplayedRowCount() < 1) {
      return;
    }

    const barcodeText = e;

    if (
      barcodeText === "" ||
      barcodeText === null ||
      barcodeText === undefined
    ) {
      return;
    }

    const focusedRowNode = getGridCurrent("grid2").focusedRowNode();

    if (
      focusedRowNode.data.fg_unit === "SN" &&
      focusedRowNode.data.cd_item !== "" &&
      focusedRowNode.data.cd_item !== null &&
      focusedRowNode.data.cd_item !== undefined
    ) {
      let hasInvalid = false;

      getGridCurrent("grid3").forEachNode((node: any) => {
        if (barcodeText.toLowerCase() === node.data.no_sn.toLowerCase()) {
          hasInvalid = true;
        }
      });

      if (hasInvalid) {
        await showDialog("alter", "이미 입력한 시리얼 번호입니다.");
        txtBarcodeRef.current.focus();
        return;
      }

      const params = {
        cd_biz: focusedRowNode.data.cd_biz,
        cd_item: focusedRowNode.data.cd_item,
        no_sn: barcodeText,
      };

      const data = await fnCheckDuplicateSn(params);

      if (data && data.length > 0) {
        await showDialog(
          "alter",
          `이미 등록된 시리얼 번호입니다: ${barcodeText}`
        );
        txtBarcodeRef.current.focus();
        return;
      }

      isScan.current = true;
      getGridCurrent("grid3").addRow();
    }
  };
  //#endregion

  //#region GridH2 Events
  const onFocusedRowChangedH2 = (event: any) => {
    const selectedRow = event; // 클릭한 행 데이터
    if (selectedRow) {
      const no_io = selectedRow.no_io;
      getGridCurrent("grid5").selectAndFocusRow(0);
      setSelectedDocu(no_io);
    } else {
      return;
    }
  };
  //#endregion

  //#region onTabChanged
  // 처리 이동으로 덮어썼던 조회조건을 원래대로 되돌린다. 보관된 값이 없으면 null.
  const restorePrevSearchParams = (): any => {
    const saved = prevSearchParams.current;
    if (!saved) return null;

    prevSearchParams.current = null;
    setFormData(saved);
    return saved;
  };

  const onTabChanged = async (index: string) => {
    if (index === "1") {
      setTabState({ page: index, searchParams: restorePrevSearchParams() });
      return;
    }
    setTabState({ page: index, searchParams: null });
  };
  //#endregion

  //#region onSnButtonClick
  const onSnButtonClick = async (id: string) => {
    const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
    if (!focusedRowNodeH) {
      return;
    }

    switch (id) {
      case "Good":
        getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
          if (node.data.yn_qc !== "Y" && node.data.fg_unit !== "SN") {
            const qt = node.data.qt;
            node.setDataValue("qt_bad", 0, "noEvent");
            node.setDataValue("qt_good", qt, "noEvent");
            node.setDataValue("qt_rcv", qt, "noEvent");
          }
        });
        break;
      case "Bad":
        getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
          if (node.data.yn_qc !== "Y" && node.data.fg_unit !== "SN") {
            const qt = node.data.qt;
            node.setDataValue("qt_good", 0, "noEvent");
            node.setDataValue("qt_bad", qt, "noEvent");
            node.setDataValue("qt_rcv", qt, "noEvent");
          }
        });
        break;
    }
  };
  //#endregion

  //#region Method
  const resetCondition = (): any => {
    setWhCode({ code: "", name: "" });
  };
  //#endregion

  //#region useMemo
  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onCellValueChanged: onCellValueChangedL,
        },
      },
      grid3: {
        columns: columns.grid3,
        dataSource: dataSources.grid3,
        events: {
          onBeforeAddRow: onBeforeAddRowD,
          onRowDataUpdated: onRowDataUpdatedSn,
          onBtnAddClick: onGridDBtnsClick,
          onBtnDeleteClick: onGridDBtnsClick,
        },
      },
      grid4: {
        columns: columns.grid4,
        dataSource: dataSources.grid4,
        events: {
          onFocusedRowChanged: onFocusedRowChangedH2,
        },
      },
      grid5: {
        columns: columns.grid5,
        dataSource: dataSources.grid5,
      },
      grid6: {
        columns: columns.grid6,
        dataSource: dataSources.grid6,
      },
    }),
    [columns, dataSources]
  );
  //#endregion

  //#region  UI
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>
      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnTab
          titles={["미처리", "처리"]}
          width={"100%"}
          selectedTab={tabPageState.page}
          onTabChanged={onTabChanged}
          header={[
            <SnTabHeader
              body={[
                <div className="relative flex space-x-2">
                  <div style={{ width: 60 }}>
                    <SnLabel text="입고일" type="string" />
                  </div>
                  <div style={{ width: 180 }}>
                    <SnDateBox
                      defaultValue={dateBox}
                      onValueChanged={dateValueChanged}
                    />
                  </div>
                  <div style={{ width: 70 }}>
                    <SnLabel text="입고창고" type="string" />
                  </div>
                  <div style={{ width: 180 }}>
                    <SnCodeBox
                      code="POPUP_WH"
                      defaultValue={{
                        code: codeWh.code,
                        name: codeWh.name,
                      }}
                      onValueChanged={whValueChanged}
                    />
                  </div>
                  <div style={{ width: 60 }}>
                    <SnLabel text="담당자" type="string" />
                  </div>
                  <div style={{ width: 180 }}>
                    <SnCodeBox
                      code="POPUP_EMP"
                      defaultValue={{
                        code: codeEmp.code,
                        name: codeEmp.name,
                      }}
                      onValueChanged={empValueChanged}
                      isEditable={true}
                    />
                  </div>
                  <div>
                    <SnButton
                      text={"양품적용"}
                      onClick={() => {
                        onSnButtonClick("Good");
                      }}
                    />
                    <SnButton
                      text={"불량적용"}
                      onClick={() => {
                        onSnButtonClick("Bad");
                      }}
                    />
                  </div>
                  <div style={{ paddingLeft: "80px" }}>
                    <SnButton
                      text={"처리"}
                      onClick={() => {
                        ExecuteRunner.run("Save");
                      }}
                    />
                  </div>
                </div>,
              ]}
            />,
            <SnTabHeader
              body={[
                <div className="relative flex space-x-2">
                  <SnButton
                    text={"취소"}
                    onClick={() => {
                      ExecuteRunner.run("Delete");
                    }}
                  />
                </div>,
              ]}
            />,
          ]}
          body={[
            <div className="flex-1 h-full min-h-0 space-y-1.5">
              <SnResizeBar direction={"height"} id="panel1" orgSize="45.5%">
                <SnGrid
                  ref={gridRefs.current["grid1"]} // Grid Name
                  columnConfig={grids.grid1.columns} // 컬럼 설정 전달
                  rowData={grids.grid1.dataSource} // Datasource
                  etcProps={{ rowSelection: "single" }}
                  filter={{
                    refs: [gridRefs.current["grid2"]],
                    columns: ["no_req"],
                  }}
                />
              </SnResizeBar>
              <SnResizeBar direction={"height"} id="panel2" orgSize="45.5%">
                <div className="flex w-full h-full min-w-0 space-x-1.5">
                  <SnResizeBar direction={"width"} id="panel3" orgSize="75%">
                    <SnGrid
                      ref={gridRefs.current["grid2"]} // Grid Name
                      columnConfig={grids.grid2.columns} // 컬럼 설정 전달
                      rowData={grids.grid2.dataSource} // Datasource
                      etcProps={{ rowSelection: "multiple", title: "상세정보" }}
                      events={grids.grid2.events}
                      filter={{
                        refs: [gridRefs.current["grid3"]],
                        columns: ["no_req", "no_line"],
                      }}
                    />
                  </SnResizeBar>
                  <div className="flex-1 min-w-0">
                    <SnGrid
                      ref={gridRefs.current["grid3"]} // Grid Name
                      columnConfig={grids.grid3.columns} // 컬럼 설정 전달
                      rowData={grids.grid3.dataSource} // Datasource
                      etcProps={{ rowSelection: "single", title: "SN" }}
                      events={grids.grid3.events}
                      enablePasteOnlyAdd={true}
                      header={
                        <div>
                          <SnBarcodeBox
                            onEnter={onBarcodeEnter}
                            ref={txtBarcodeRef}
                            inputRef={useRef(null)}
                          />
                        </div>
                      }
                    />
                  </div>
                </div>
              </SnResizeBar>
            </div>,
            <div className="flex-1 h-full min-h-0 space-y-1.5">
              <SnResizeBar direction={"height"} id="panel5" orgSize="45.5%">
                <SnGrid
                  ref={gridRefs.current["grid4"]} // Grid Name
                  columnConfig={grids.grid4.columns} // 컬럼 설정 전달
                  rowData={grids.grid4.dataSource} // Datasource
                  etcProps={{ rowSelection: "single" }}
                  events={grids.grid4.events}
                  filter={{
                    refs: [gridRefs.current["grid5"]],
                    columns: ["no_io"],
                  }}
                />
              </SnResizeBar>
              <SnResizeBar direction={"height"} id="panel6" orgSize="45.5%">
                <div className="flex w-full h-full min-w-0 space-x-1.5">
                  <SnResizeBar direction={"width"} id="panel7" orgSize="75%">
                    <SnGrid
                      ref={gridRefs.current["grid5"]} // Grid Name
                      columnConfig={grids.grid5.columns} // 컬럼 설정 전달
                      rowData={grids.grid5.dataSource} // Datasource
                      etcProps={{ rowSelection: "multiple", title: "상세정보" }}
                      filter={{
                        refs: [gridRefs.current["grid6"]],
                        columns: ["no_io", "no_line"],
                      }}
                    />
                  </SnResizeBar>
                  <div className="flex-1 min-w-0">
                    <SnGrid
                      ref={gridRefs.current["grid6"]} // Grid Name
                      columnConfig={grids.grid6.columns} // 컬럼 설정 전달
                      rowData={grids.grid6.dataSource} // Datasource
                      etcProps={{ rowSelection: "multiple", title: "SN" }}
                    />
                  </div>
                </div>
              </SnResizeBar>
              <SnFileUploadBox
                selectedDocu={selectedDocu}
                selectedLineDocu={0}
              />
            </div>,
          ]}
        />
      </div>
    </div>
  );
  //#endregion
});

export default SN_MTL_RCV_REG_004;
